import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Badge, Button, Card, Skeleton } from '@/ui/primitives'
import { Grup } from './comune'

type Pas = {
  step: number; type: string; endpoint: string; description: string
  required: boolean; file_example: string; import_frequency: string; frequency_badge: string
}
type Rezultat = { success: boolean; message?: string; records?: number }
type Istoric = { id: number; type: string; filename: string | null; records: number | null; status: string; created_at?: string | null }

const clipa = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('ro-RO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

function PasImport({ p, gata }: { p: Pas; gata: () => void }) {
  const camp = useRef<HTMLInputElement>(null)
  const [rezultat, setRezultat] = useState<string | null>(null)
  const [reusit, setReusit] = useState<boolean | null>(null)

  const incarca = useMutation({
    /** Endpointul vine din raspunsul serverului si e deja relativ la /api (ex. „/upload/partners"),
     *  deci se foloseste ca atare — nu se rescrie in cod, ca sa nu divergem de server. */
    mutationFn: (fisier: File) => api.incarca<Rezultat>(p.endpoint, fisier),
    onSuccess: (r) => {
      setReusit(r.success)
      setRezultat(r.success ? `${r.records ?? 0} inregistrari — ${r.message ?? 'gata'}` : (r.message ?? 'Import esuat'))
      if (camp.current) camp.current.value = ''
      gata()
    },
    onError: (e) => { setReusit(false); setRezultat(e instanceof Error ? e.message : 'Import esuat') },
  })

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="h-7 w-7 shrink-0 rounded-full bg-page flex items-center justify-center text-[13px] tnum">
            {p.step}
          </span>
          <div>
            <p className="text-[14.5px] font-medium">{p.description}</p>
            <p className="mt-0.5 text-[12.5px] text-muted">
              Fisier de forma <span className="tnum">{p.file_example}</span> · {p.import_frequency}
            </p>
          </div>
        </div>
        <Badge tone={p.required ? 'warn' : 'neutral'}>{p.required ? 'obligatoriu' : 'optional'}</Badge>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2.5">
        <input ref={camp} type="file" accept=".csv" className="text-[13px]" />
        <Button variant="quiet" disabled={incarca.isPending}
          onClick={() => {
            const f = camp.current?.files?.[0]
            if (!f) { setReusit(false); setRezultat('Alege intai un fisier'); return }
            setRezultat(null); setReusit(null); incarca.mutate(f)
          }}>
          {incarca.isPending ? 'Se incarca…' : 'Incarca'}
        </Button>
        {rezultat && (
          <span className={`text-[13px] ${reusit ? 'text-good' : 'text-bad'}`}>{rezultat}</span>
        )}
      </div>
    </Card>
  )
}

export function SetariImport() {
  const qc = useQueryClient()
  const pasi = useQuery({
    queryKey: ['import-pasi'],
    queryFn: () => api.get<{ import_order: Pas[] }>('/upload/import/status'),
  })
  const istoric = useQuery({
    queryKey: ['import-istoric'],
    queryFn: () => api.get<{ imports: Istoric[] }>('/upload/import/history', { period: 'last_month' }),
  })

  if (pasi.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={6} /></Card></div>

  return (
    <div className="px-8 py-6 space-y-5">
      <Card className="p-4">
        <p className="text-[13.5px] text-muted">
          Fisierele exportate din QTravel Backoffice se incarca in ordinea de mai jos: partenerii
          intai, ca facturile sa aiba pe cine lega. In mod normal le aduce sincronizarea automata —
          asta e calea de rezerva.
        </p>
      </Card>

      <div className="space-y-3">
        {(pasi.data?.import_order ?? []).map((p) => (
          <PasImport key={p.step} p={p}
            gata={() => qc.invalidateQueries({ queryKey: ['import-istoric'] })} />
        ))}
      </div>

      <Grup titlu="Importuri in ultima luna">
        {istoric.data?.imports?.length ? (
          <ul className="space-y-1.5 text-[13.5px]">
            {istoric.data.imports.map((i) => (
              <li key={i.id} className="flex flex-wrap gap-x-2">
                <span className="text-muted tnum">{clipa(i.created_at)}</span>
                <span>{i.type}</span>
                <span className="text-muted">{i.filename ?? '—'}</span>
                <span className="tnum">{i.records ?? 0}</span>
                <Badge tone={i.status === 'success' ? 'good' : 'bad'}>{i.status}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13.5px] text-muted">Niciun import in ultima luna.</p>
        )}
      </Grup>
    </div>
  )
}
